// AdReportCard — the PUBLIC card an /adreport posts into the Reporting channel: Adscope's marketing
// report for the period (spend, reach, clicks, conversions, platform split, per-product rows, the
// executive summary) plus "Download PDF", which fetches the stored report snapshot and renders The
// Media Crowd's Performance Marketing Report PDF in the browser (lib/adReportPdf.js, loaded on demand).
// Deletable like any message: the requester (owner) gets a ✕, which routes through the normal
// delete confirm → DeleteMessage; the server enforces owner-only.
import { useState } from "react";
import { api } from "../hooks/useApi.js";

const fmtR = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = (n) => Number(n || 0).toLocaleString("en-ZA");
const fmtDay = (iso) => {
  try { return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return iso; }
};
const fmtRange = (from, to) => {
  if (!from || !to) return "";
  const y = to.slice(0, 4);
  return `${fmtDay(from)} – ${fmtDay(to)} ${y}`;
};
const PLATFORM_COLOR = { Google: "#ea580c", Meta: "#2563eb" };

export default function AdReportCard({ m, cmeta, currentUserId, onDelete, time }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const mine = String(m.senderUserId) === String(currentUserId);
  const platforms = Array.isArray(cmeta.platforms) ? cmeta.platforms : [];
  const products = Array.isArray(cmeta.products) ? cmeta.products : [];

  async function download() {
    if (busy || !cmeta.reportId) return;
    setBusy(true);
    setErr(null);
    try {
      const data = await api.get(`/api/marketing-reports/${cmeta.reportId}`);
      const { downloadMarketingReportPdf } = await import("../lib/adReportPdf.js");
      downloadMarketingReportPdf(data);
    } catch (e) {
      setErr(e?.status === 404 ? "This report's data is no longer stored." : "Couldn't build the PDF — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dt-card adr-card">
      <div className="dt-card__head">
        <span className="dt-card__icon">📈</span>
        <div className="dt-card__ht">
          <strong className="dt-card__title">{cmeta.company || "Affinity"} — marketing report</strong>
          <span className="dt-card__sub">{fmtRange(cmeta.from, cmeta.to)}{cmeta.label ? ` · ${cmeta.label}` : ""}{cmeta.by ? ` · by ${cmeta.by}` : ""}</span>
        </div>
        {mine && onDelete && (
          <button type="button" className="meetingcard__del" title="Delete this report" onClick={() => onDelete(m)}>✕</button>
        )}
        <span className="card-time">{time}</span>
      </div>

      <div className="dt-stats adr-stats">
        <div className="dt-stat"><b>{fmtR(cmeta.totalSpend)}</b><span>spend</span></div>
        <div className="dt-stat"><b>{fmtN(cmeta.impressions)}</b><span>impressions</span></div>
        <div className="dt-stat"><b>{fmtN(cmeta.clicks)}</b><span>clicks</span></div>
        <div className="dt-stat"><b>{fmtN(cmeta.conversions)}</b><span>conversions</span></div>
      </div>

      {platforms.length > 0 && (
        <div className="dt-bars adr-bars">
          {platforms.map((p) => (
            <div className="dt-bar" key={p.platform}>
              <span className="dt-bar__label">{p.platform === "Meta" ? "Meta Ads" : p.platform === "Google" ? "Google Ads" : p.platform}</span>
              <span className="dt-bar__track">
                <span className="dt-bar__fill" style={{ width: `${Math.min(100, Math.max(0, p.pct || 0))}%`, background: PLATFORM_COLOR[p.platform] || undefined }} />
              </span>
              <span className="dt-bar__val">{fmtR(p.spend)} · {(p.pct || 0).toFixed(1)}%</span>
            </div>
          ))}
        </div>
      )}

      {products.length > 0 && (
        <div className="dt-table adr-table" role="table">
          <div className="dt-table__row dt-table__row--head" role="row">
            <span className="dt-table__c dt-table__c--name">Product</span>
            <span className="dt-table__c">Platform</span>
            <span className="dt-table__c">Spend</span>
            <span className="dt-table__c">Conv</span>
          </div>
          {products.map((p, i) => (
            <div className="dt-table__row" role="row" key={`${p.name}-${p.platform}-${i}`}>
              <span className="dt-table__c dt-table__c--name" title={p.name}>{p.name}</span>
              <span className="dt-table__c adr-plat" style={{ color: PLATFORM_COLOR[p.platform] || undefined }}>{p.platform}</span>
              <span className="dt-table__c">{fmtR(p.spend)}</span>
              <span className="dt-table__c">{fmtN(p.conversions)}</span>
            </div>
          ))}
        </div>
      )}

      {cmeta.summary ? <p className="adr-card__summary">{cmeta.summary}</p> : null}

      <div className="adr-card__foot">
        <span className="adr-card__src">{err ? <span className="adr-card__err">{err}</span> : cmeta.insightsSource === "fallback" ? "Insights: standard copy (AI unavailable)" : "Source: Adscope"}</span>
        <button type="button" className="adr-card__dl" disabled={busy || !cmeta.reportId} onClick={download}>
          {busy ? <span className="btn__spin" /> : "⬇"} {busy ? "Building PDF…" : "Download PDF"}
        </button>
      </div>
    </div>
  );
}
